import { useEffect, useState } from 'react';

import MainSkeleton from '@src/components/MainSkeleton';
import useIsLoggedIn from '@src/hooks/useIsLoggedIn';
import {
	useAppointmentSelectionStore,
	useClientInformationStore,
	useFacilityStore,
	usePatientInformationStore,
	usePetsInformationStore,
	useReasonForVisitStore,
	useRouteHandlerStore,
	useTimeSlotStore,
} from '@src/store';
import useGlobalUXStore from '@src/store/modules/global-ux.store';

const withStoreReset = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
	const WithStoreReset = (props: P) => {
		const [ready, setReady] = useState(false);
		const { isLoggedIn, checked } = useIsLoggedIn();

		const { reset: resetGlobalUXStore } = useGlobalUXStore((store) => store);
		const { reset: resetAppointmentSelectionStore } = useAppointmentSelectionStore((store) => store);
		const { reset: resetFacilityStore } = useFacilityStore((store) => store);
		const { reset: resetTimeSlotStore } = useTimeSlotStore((store) => store);
		const { reset: resetReasonForVisitStore } = useReasonForVisitStore((store) => store);
		const { reset: resetPatientInformationStore } = usePatientInformationStore((store) => store);
		const { reset: resetClientInformationStore } = useClientInformationStore((store) => store);
		const { reset: resetRouteHandlerStore } = useRouteHandlerStore((store) => store);
		const { reset: resetPetsInformationStore } = usePetsInformationStore((store) => store);

		useEffect(() => {
			if (!ready && checked) {
				resetGlobalUXStore();
				resetTimeSlotStore();
				resetAppointmentSelectionStore();
				resetReasonForVisitStore();
				resetPatientInformationStore();
				resetRouteHandlerStore();
				if (!isLoggedIn) {
					// we want to preserve various information on page navigation for logged in users
					resetClientInformationStore();
					resetPetsInformationStore();
				}
				setReady(true);
			}
		}, [resetTimeSlotStore, resetFacilityStore, resetReasonForVisitStore, ready, checked, isLoggedIn]);

		if (!ready) {
			return <MainSkeleton />;
		}

		return <WrappedComponent {...props} />;
	};

	return WithStoreReset;
};

export default withStoreReset;
